// Phase 9 correction (item 57): the earlier version of this page had the
// admin type the auditor's real login password into a plaintext field on
// screen. That field is gone. The backend now generates a random temporary
// password itself and returns it exactly once in the create/reset-password
// response - this page's only job is to show that one-time value clearly
// (with a Copy button) and never persist or re-display it again.
import { useEffect, useState } from "react";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { Plus, Copy, Mail } from "lucide-react";
import { toast } from "sonner";
import { Field, SectionCard, StatusPill } from "./shared";
import { auditorBlank } from "./constants";

function OneTimePasswordDialog({ result, onClose }) {
  if (!result) return null;
  const copy = () => {
    navigator.clipboard?.writeText(result.temporaryPassword);
    toast.success("Copied to clipboard");
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-sm bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 text-sm font-extrabold text-[#111827]">One-time temporary password</div>
        <p className="mb-3 text-xs text-[#6B7280]">
          This is shown only once and is not stored anywhere in plaintext - copy it now and share it with the auditor securely.
          {result.emailSent ? " It has also been emailed to them." : ""}
        </p>
        <div className="flex items-center gap-2 rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] p-3">
          <code className="flex-1 break-all text-sm font-semibold text-[#111827]">{result.temporaryPassword}</code>
          <Button size="sm" variant="outline" onClick={copy}><Copy className="h-3.5 w-3.5" /></Button>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>
  );
}

export default function AuditorsPage() {
  const [auditors, setAuditors] = useState([]);
  const [form, setForm] = useState(auditorBlank);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [oneTime, setOneTime] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/society/auditors").catch(() => ({ data: [] }));
      setAuditors(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(formatErr(err, "Could not load auditors"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addAuditor = async () => {
    try {
      setBusy(true);
      const res = await api.post("/society/auditors", form);
      toast.success("Auditor login created");
      setForm(auditorBlank);
      setOneTime(res.data);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not create auditor"));
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async (auditor) => {
    if (!window.confirm(`Reset the password for ${auditor.name}? Their current password will stop working.`)) return;
    try {
      setBusy(true);
      const res = await api.post(`/society/auditors/${auditor.id}/reset-password`, { sendEmail: false });
      setOneTime(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not reset password"));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (auditor, status) => {
    try {
      setBusy(true);
      if (status === "suspended") {
        await api.delete(`/society/auditors/${auditor.id}`);
      } else {
        await api.patch(`/society/auditors/${auditor.id}`, { status });
      }
      toast.success(status === "suspended" ? "Auditor suspended" : "Auditor reactivated");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update auditor"));
    } finally {
      setBusy(false);
    }
  };

  const clearExpiry = async (auditor) => {
    try {
      setBusy(true);
      await api.patch(`/society/auditors/${auditor.id}`, { clearExpiry: true });
      toast.success("Expiry removed");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update auditor"));
    } finally {
      setBusy(false);
    }
  };

  const isExpired = (a) => a.expiresAt && new Date(a.expiresAt) < new Date(new Date().toDateString());

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Auditor / CA Access</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Create read-only CA / auditor logins, time-bound if needed. Passwords are generated by the server and shown once - never typed or stored in plaintext here.</p>
      </div>

      <SectionCard title="CA / auditor logins">
        <div className="grid gap-3 rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-4 md:grid-cols-3">
          <Field label="Auditor name"><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></Field>
          <Field label="Firm name"><Input value={form.firmName} onChange={(e) => setForm((f) => ({ ...f, firmName: e.target.value }))} /></Field>
          <Field label="Membership no."><Input value={form.membershipNo} onChange={(e) => setForm((f) => ({ ...f, membershipNo: e.target.value }))} /></Field>
          <Field label="Access expires on (optional)"><Input type="date" value={form.expiresAt} onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))} /></Field>
          <div className="md:col-span-3 flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-[#374151]">
              <input type="checkbox" className="h-3.5 w-3.5 rounded-sm border-[#D1D5DB]" checked={form.sendEmail} onChange={(e) => setForm((f) => ({ ...f, sendEmail: e.target.checked }))} />
              Email the one-time password to the auditor (if email is configured on the server)
            </label>
            <Button disabled={busy || !form.name || !form.email} onClick={addAuditor} className="bg-[#111827] text-white hover:bg-[#374151]">
              <Plus className="mr-2 h-4 w-4" /> Create auditor login
            </Button>
          </div>
        </div>

        <div className="mt-4 overflow-x-auto rounded-sm border border-[#E5E7EB]">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Firm</TableHead><TableHead>Expires</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-[#6B7280]">Loading...</TableCell></TableRow>
              ) : auditors.map((auditor) => (
                <TableRow key={auditor.id}>
                  <TableCell className="font-semibold">{auditor.name}</TableCell>
                  <TableCell>{auditor.email}</TableCell>
                  <TableCell>{auditor.firmName || "-"}</TableCell>
                  <TableCell className="text-xs">
                    {auditor.expiresAt ? (
                      <span className={isExpired(auditor) ? "font-semibold text-red-600" : ""}>
                        {auditor.expiresAt}{isExpired(auditor) ? " (expired)" : ""}
                        {" "}<button type="button" className="ml-1 text-[#6B7280] underline" onClick={() => clearExpiry(auditor)}>clear</button>
                      </span>
                    ) : "Never"}
                  </TableCell>
                  <TableCell><StatusPill active={auditor.status !== "suspended"} trueLabel="Active" falseLabel="Suspended" /></TableCell>
                  <TableCell className="text-right space-x-2 whitespace-nowrap">
                    <Button variant="outline" size="sm" disabled={busy} onClick={() => resetPassword(auditor)}>Reset Password</Button>
                    {auditor.status !== "suspended" ? (
                      <Button variant="outline" size="sm" disabled={busy} onClick={() => setStatus(auditor, "suspended")}>Suspend</Button>
                    ) : (
                      <Button variant="outline" size="sm" disabled={busy} onClick={() => setStatus(auditor, "active")}>Reactivate</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && auditors.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-[#6B7280]">No auditor logins created.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </SectionCard>

      <SectionCard title="Auditor permissions" subtitle="These apply automatically to every auditor login - no per-user configuration needed.">
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700">Allowed (read-only)</div>
            <ul className="list-disc space-y-1 pl-5 text-[#374151]">
              <li>All Accounting reports (Trial Balance, Ledger, Bank Book, Cash Book, Balance Sheet, etc.)</li>
              <li>Voucher view, Chart of Accounts, Ledgers, Vendors, Bank Accounts (view only)</li>
              <li>Audit Trail and Bank Reconciliation workspace (view only)</li>
            </ul>
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-red-700">Never allowed</div>
            <ul className="list-disc space-y-1 pl-5 text-[#374151]">
              <li>Creating, editing, posting or cancelling any voucher</li>
              <li>Matching/unmatching bank reconciliation entries, importing statements</li>
              <li>Changing masters, Accounting Period Lock, Financial Years, or other auditor logins</li>
            </ul>
          </div>
        </div>
        <p className="mt-3 flex items-center gap-1 text-xs text-[#6B7280]"><Mail className="h-3.5 w-3.5" /> Time-bound access expires automatically on the chosen date - no manual step is needed to cut off access.</p>
      </SectionCard>

      <OneTimePasswordDialog result={oneTime} onClose={() => setOneTime(null)} />
    </div>
  );
}
